import React, { useState, useEffect, Fragment } from 'react';
import { 
  Search, Filter, ArrowLeft, User, ShoppingBag, Calendar, 
  ChevronDown, ChevronUp, Download, Mail, Phone,
  FileText, RefreshCw, DollarSign
} from 'lucide-react';
import { Member } from '../types';

interface AdminMembersProps {
  members: Member[];
  onNavigate: (page: string) => void;
}

interface ApiResponse {
  success: boolean;
  data: Member[];
  count: number;
  totalRevenue?: number;
}

type MemberTier = 'Sapphire' | 'Ruby' | 'Emerald' | 'Diamond';

// Tier configuration with point thresholds
const TIER_CONFIG: Record<MemberTier, { minPoints: number; maxPoints: number; label: MemberTier; color: string; icon: string }> = {
  Sapphire: { minPoints: 0, maxPoints: 4999, label: 'Sapphire', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: '💎' },
  Ruby: { minPoints: 5000, maxPoints: 14999, label: 'Ruby', color: 'bg-red-100 text-red-700 border-red-200', icon: '❤️' },
  Emerald: { minPoints: 15000, maxPoints: 49999, label: 'Emerald', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: '✨' },
  Diamond: { minPoints: 50000, maxPoints: Infinity, label: 'Diamond', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: '👑' }
};

// Calculate tier based on points
const getTierFromPoints = (points: number): MemberTier => {
  if (points >= 50000) return 'Diamond';
  if (points >= 15000) return 'Emerald';
  if (points >= 5000) return 'Ruby';
  return 'Sapphire';
};

// Calculate points from total spent (100 PHP = 1 point)
const calculatePointsFromSpent = (totalSpent: number): number => {
  return Math.floor(totalSpent / 100);
};

// API base URL
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://sparklinggems.shop/api'
  : 'http://localhost/sparkling-gems-backend-php/api';

const AdminMembers: React.FC<AdminMembersProps> = ({ members, onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedMember, setExpandedMember] = useState<number | null>(null);
  const [memberOrders, setMemberOrders] = useState<Record<number, any[]>>({});
  const [loadingOrders, setLoadingOrders] = useState<Record<number, boolean>>({});
  
  // State for filtering and sorting
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'spent'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [localMembers, setLocalMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiTotalRevenue, setApiTotalRevenue] = useState<number | null>(null);

  // Fetch members from database (ONLY members, exclude admins)
  const fetchMembersFromDatabase = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching members from /api/members/index.php');
      const response = await fetch(`${API_BASE_URL}/members/index.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Members API response:', result);
      
      if (result.success && Array.isArray(result.data)) {
        console.log('Raw members data count:', result.data.length);
        
        // Filter out admin users - check multiple possible role field names
        const filteredMembers = result.data.filter((m: any) => {
          const role = m.role || m.user_role || '';
          const isAdmin = role === 'admin' || role === 'Admin' || role === 'ADMIN';
          
          if (isAdmin) {
            console.log(`⛔ Excluding admin user: ${m.name || m.email || m.username} (role: ${role})`);
            return false;
          }
          return true;
        });
        
        console.log(`Filtered members (admins removed): ${filteredMembers.length} members`);
        
        // Calculate proper tier based on points/total_spent
        const membersWithTier: Member[] = filteredMembers.map((m: any) => {
          // Calculate points from total spent (100 PHP = 1 point)
          let points = m.points || 0;
          const totalSpent = m.total_spent || m.totalSpent || 0;
          
          // If points is 0 but totalSpent > 0, calculate points from totalSpent
          if (points === 0 && totalSpent > 0) {
            points = calculatePointsFromSpent(totalSpent);
            console.log(`Calculated points for ${m.name || m.email}: ${totalSpent} spent = ${points} points`);
          }
          
          // Determine tier based on points
          const tier = getTierFromPoints(points);
          
          console.log(`Member: ${m.name || m.email}, Points: ${points}, Tier: ${tier}, Spent: ${totalSpent}, Role: ${m.role}`);
          
          return {
            id: m.id,
            name: m.name || m.first_name + ' ' + m.last_name || m.username || 'Member',
            email: m.email,
            phone: m.phone || '',
            avatar: m.avatar || '',
            role: m.role || 'member',
            membership_tier: tier,
            points: points,
            status: m.status || 'Active',
            totalSpent: totalSpent,
            ordersCount: m.orders_count || m.ordersCount || 0,
            joinDate: m.joinDate || m.created_at?.split('T')[0] || new Date().toLocaleDateString(),
            created_at: m.created_at,
            last_login: m.last_login
          };
        });
        
        setLocalMembers(membersWithTier);
        
        if (result.totalRevenue !== undefined) {
          setApiTotalRevenue(result.totalRevenue);
        }
        console.log(`✅ Loaded ${membersWithTier.length} members (admins filtered out)`);
        console.log('📊 Member tiers:', membersWithTier.map(m => ({ name: m.name, tier: m.membership_tier, points: m.points, spent: m.totalSpent })));
      } else {
        console.warn('Unexpected API response format:', result);
        // Filter props members
        const filtered = members.filter(m => {
          const role = m.role || '';
          return role !== 'admin' && role !== 'Admin' && role !== 'ADMIN';
        });
        setLocalMembers(filtered);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
      setError('Failed to load members. Using sample data.');
      const filtered = members.filter(m => {
        const role = m.role || '';
        return role !== 'admin' && role !== 'Admin' && role !== 'ADMIN';
      });
      setLocalMembers(filtered);
    } finally {
      setLoading(false);
    }
  };

  // Fetch orders for a specific member
  const fetchMemberOrders = async (userId: number) => {
    setLoadingOrders(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/orders/user.php?user_id=${userId}`);
      const data = await response.json();
      
      if (data.success && data.orders) {
        setMemberOrders(prev => ({ ...prev, [userId]: data.orders }));
        console.log(`Loaded ${data.orders.length} orders for user ${userId}`);
      } else {
        setMemberOrders(prev => ({ ...prev, [userId]: [] }));
      }
    } catch (error) {
      console.error(`Error fetching orders for user ${userId}:`, error);
      setMemberOrders(prev => ({ ...prev, [userId]: [] }));
    } finally {
      setLoadingOrders(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Fetch on component mount
  useEffect(() => {
    fetchMembersFromDatabase();
  }, []);

  // Fetch orders when expanding a member
  useEffect(() => {
    if (expandedMember !== null) {
      const member = localMembers.find(m => m.id === expandedMember);
      if (member && member.id && !memberOrders[member.id]) {
        fetchMemberOrders(member.id);
      }
    }
  }, [expandedMember, localMembers, memberOrders]);

  // Filter and sort members (exclude admins)
  const filteredMembers = localMembers
    .filter(m => {
      // Exclude any admin users
      const role = m.role || '';
      if (role === 'admin' || role === 'Admin' || role === 'ADMIN') return false;
      
      const matchesSearch = 
        (m.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || 
        (m.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (m.phone?.toLowerCase() || '').includes(searchTerm.toLowerCase());
      
      const matchesStatus = filterStatus === 'all' || m.status === filterStatus;
      const matchesTier = filterTier === 'all' || m.membership_tier === filterTier;
      
      return matchesSearch && matchesStatus && matchesTier;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'date':
          const dateA = a.joinDate ? new Date(a.joinDate).getTime() : 
                       a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.joinDate ? new Date(b.joinDate).getTime() : 
                       b.created_at ? new Date(b.created_at).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case 'spent':
          comparison = (a.totalSpent || 0) - (b.totalSpent || 0);
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Calculate stats (only members, not admins)
  const totalMembers = localMembers.length;
  const activeMembers = localMembers.filter(m => m.status === 'Active' || m.status === 'VIP').length;
  const vipMembers = localMembers.filter(m => m.membership_tier === 'Diamond' || m.membership_tier === 'Emerald').length;
  const totalRevenue = apiTotalRevenue !== null ? apiTotalRevenue : localMembers.reduce((sum, m) => sum + (m.totalSpent || 0), 0);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Get tier badge with icon
  const getTierBadge = (tier: string = 'Sapphire') => {
    const config = TIER_CONFIG[tier as MemberTier] || TIER_CONFIG.Sapphire;
    return config.color;
  };

  const getTierIcon = (tier: string = 'Sapphire') => {
    const config = TIER_CONFIG[tier as MemberTier] || TIER_CONFIG.Sapphire;
    return config.icon;
  };

  // Get status badge
  const getStatusBadge = (status: string = 'Active') => {
    switch(status) {
      case 'VIP':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'Active':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'Inactive':
        return 'bg-stone-100 text-stone-500 border-stone-200';
      default:
        return 'bg-stone-100 text-stone-500 border-stone-200';
    }
  };

  // Download as CSV
  const downloadCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Membership Tier', 'Points', 'Status', 'Join Date', 'Total Spent', 'Orders Count'];
    
    const csvData = filteredMembers.map(m => [
      m.name || '',
      m.email || '',
      m.phone || '',
      m.membership_tier || 'Sapphire',
      m.points || 0,
      m.status || 'Active',
      m.joinDate || m.created_at || '',
      m.totalSpent || 0,
      m.ordersCount || 0
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `sparkling-gems-members-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleExpand = (id: number) => {
    setExpandedMember(expandedMember === id ? null : id);
  };

  // Helper to get member orders
  const getMemberOrders = (memberId: number): any[] => {
    return memberOrders[memberId] || [];
  };

  // Format order status for display
  const getOrderStatusBadge = (status: string) => {
    const statusLower = status?.toLowerCase() || '';
    switch(statusLower) {
      case 'delivered':
      case 'picked up':
        return 'bg-green-100 text-green-700';
      case 'shipped':
        return 'bg-blue-100 text-blue-700';
      case 'preparing':
      case 'processing':
        return 'bg-purple-100 text-purple-700';
      case 'paid':
        return 'bg-emerald-100 text-emerald-700';
      case 'pending':
        return 'bg-orange-100 text-orange-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-stone-100 text-stone-600';
    }
  };

  return (
    <div className="pt-44 pb-12 min-h-screen bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
            <div>
                 <button onClick={() => onNavigate('admin-dashboard')} className="flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors">
                    <ArrowLeft size={14} /> Back to Dashboard
                 </button>
                <h1 className="font-serif text-3xl text-stone-900">Member Directory</h1>
                <p className="text-stone-500 font-light mt-2 text-sm">Manage all registered members and their information.</p>
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center gap-3">
                <button
                  onClick={fetchMembersFromDatabase}
                  className="px-4 py-2 border border-stone-200 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors rounded-sm flex items-center gap-2"
                  disabled={loading}
                >
                  <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> 
                  {loading ? 'Loading...' : 'Refresh'}
                </button>
                <div className="relative group">
                  <button className="px-4 py-2 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors rounded-sm flex items-center gap-2">
                    <Download size={14} /> Export
                  </button>
                  <div className="absolute right-0 top-full mt-2 w-40 bg-white border border-stone-200 shadow-lg rounded-sm py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <button
                      onClick={downloadCSV}
                      className="w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 flex items-center gap-2"
                    >
                      <FileText size={12} /> CSV File
                    </button>
                  </div>
                </div>
            </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 mb-6 rounded-sm">
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-stone-200 shadow-sm rounded-sm p-6">
            <p className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">Total Members</p>
            <p className="font-serif text-3xl text-stone-900">{totalMembers}</p>
            <p className="text-[10px] text-stone-400 mt-2">Registered members</p>
          </div>
          
          <div className="bg-white border border-stone-200 shadow-sm rounded-sm p-6">
            <p className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">Active Members</p>
            <p className="font-serif text-3xl text-stone-900">{activeMembers}</p>
            <p className="text-[10px] text-green-600 mt-2">{totalMembers > 0 ? ((activeMembers/totalMembers)*100).toFixed(1) : 0}% of total</p>
          </div>
          
          <div className="bg-white border border-stone-200 shadow-sm rounded-sm p-6">
            <p className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">VIP Members</p>
            <p className="font-serif text-3xl text-stone-900">{vipMembers}</p>
            <p className="text-[10px] text-gold-600 mt-2">Diamond & Emerald tier</p>
          </div>
          
          <div className="bg-white border border-stone-200 shadow-sm rounded-sm p-6">
            <p className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">Total Revenue</p>
            <p className="font-serif text-3xl text-stone-900">{formatCurrency(totalRevenue)}</p>
            <p className="text-[10px] text-stone-400 mt-2">From member purchases</p>
          </div>
        </div>

        <div className="bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden">
            {/* Toolbar */}
            <div className="p-6 border-b border-stone-100 flex flex-col md:flex-row gap-4 justify-between bg-stone-50/30">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                    <input 
                        type="text" 
                        placeholder="Search by name or email..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-sm focus:border-gold-500 focus:outline-none text-sm"
                    />
                </div>
                
                {/* Filters and sort */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Filter size={14} className="text-stone-400" />
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="border border-stone-200 rounded-sm px-3 py-2 text-sm focus:border-gold-500 focus:outline-none bg-white"
                    >
                      <option value="all">All Status</option>
                      <option value="Active">Active</option>
                      <option value="VIP">VIP</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <select
                    value={filterTier}
                    onChange={(e) => setFilterTier(e.target.value)}
                    className="border border-stone-200 rounded-sm px-3 py-2 text-sm focus:border-gold-500 focus:outline-none bg-white"
                  >
                    <option value="all">All Tiers</option>
                    <option value="Sapphire">Sapphire</option>
                    <option value="Ruby">Ruby</option>
                    <option value="Emerald">Emerald</option>
                    <option value="Diamond">Diamond</option>
                  </select>

                  <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [newSortBy, newSortOrder] = e.target.value.split('-');
                      setSortBy(newSortBy as any);
                      setSortOrder(newSortOrder as 'asc' | 'desc');
                    }}
                    className="border border-stone-200 rounded-sm px-3 py-2 text-sm focus:border-gold-500 focus:outline-none bg-white"
                  >
                    <option value="date-desc">Newest First</option>
                    <option value="date-asc">Oldest First</option>
                    <option value="name-asc">Name A-Z</option>
                    <option value="name-desc">Name Z-A</option>
                    <option value="spent-desc">Highest Spent</option>
                    <option value="spent-asc">Lowest Spent</option>
                  </select>
                </div>
            </div>

            {/* Table */}
            {loading ? (
              <div className="p-12 text-center">
                <div className="w-8 h-8 border-2 border-gold-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-stone-500 text-sm">Loading members...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-stone-100 text-stone-500 text-[10px] uppercase tracking-widest font-bold">
                        <tr>
                          <th className="px-6 py-4">Member Info</th>
                          <th className="px-6 py-4">Status</th>
                          <th className="px-6 py-4">Tier</th>
                          <th className="px-6 py-4">Points</th>
                          <th className="px-6 py-4">Joined</th>
                          <th className="px-6 py-4 text-right">Total Spent</th>
                          <th className="px-6 py-4 text-center">Orders</th>
                          <th className="px-6 py-4"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                        {filteredMembers.length > 0 ? (
                          filteredMembers.map((member) => {
                            const orders = getMemberOrders(member.id);
                            const tierIcon = getTierIcon(member.membership_tier);
                            
                            return (
                              <Fragment key={member.id}>
                                <tr className={`hover:bg-stone-50/50 transition-colors cursor-pointer ${expandedMember === member.id ? 'bg-stone-50' : ''}`} onClick={() => toggleExpand(member.id)}>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center text-stone-500 overflow-hidden">
                                                {member.avatar ? (
                                                  <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" />
                                                ) : (
                                                  <User size={18} />
                                                )}
                                            </div>
                                            <div>
                                                <span className="font-serif text-stone-900 block">{member.name || 'No Name'}</span>
                                                <div className="flex items-center gap-2 text-xs text-stone-500 mt-1">
                                                  <Mail size={10} /> {member.email}
                                                </div>
                                                {member.phone && (
                                                  <div className="flex items-center gap-2 text-xs text-stone-500 mt-0.5">
                                                    <Phone size={10} /> {member.phone}
                                                  </div>
                                                )}
                                            </div>
                                        </div>
                                      </td>
                                    <td className="px-6 py-4">
                                        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${getStatusBadge(member.status)}`}>
                                            {member.status || 'Active'}
                                        </span>
                                      </td>
                                    <td className="px-6 py-4">
                                        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${getTierBadge(member.membership_tier)} flex items-center gap-1 w-fit`}>
                                            <span>{tierIcon}</span> {member.membership_tier || 'Sapphire'}
                                        </span>
                                      </td>
                                    <td className="px-6 py-4 text-sm font-bold text-gold-600">
                                        {member.points?.toLocaleString() || 0}
                                      </td>
                                    <td className="px-6 py-4 text-xs text-stone-600">
                                        <div className="flex items-center gap-2">
                                            <Calendar size={14} className="text-stone-400" />
                                            {member.joinDate || (member.created_at ? new Date(member.created_at).toLocaleDateString() : 'N/A')}
                                        </div>
                                      </td>
                                    <td className="px-6 py-4 text-right font-mono text-sm text-stone-900">
                                        <div className="flex items-center justify-end gap-1">
                                          <DollarSign size={12} className="text-gold-600" />
                                          {member.totalSpent?.toLocaleString() || '0'}
                                        </div>
                                      </td>
                                    <td className="px-6 py-4 text-center text-sm font-bold">
                                        {member.ordersCount || 0}
                                      </td>
                                    <td className="px-6 py-4 text-right text-stone-400">
                                        {expandedMember === member.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                      </td>
                                </tr>
                                
                                {/* Expanded Details Row with Real Orders */}
                                {expandedMember === member.id && (
                                  <tr>
                                    <td colSpan={8} className="bg-stone-50 p-6 border-b border-stone-200 shadow-inner">
                                        <div className="max-w-5xl mx-auto">
                                            <h4 className="text-xs uppercase tracking-widest font-bold text-stone-500 mb-4 flex items-center gap-2">
                                                <ShoppingBag size={14} /> Recent Purchase History
                                            </h4>
                                            
                                            {loadingOrders[member.id] ? (
                                              <div className="flex justify-center py-8">
                                                <div className="w-6 h-6 border-2 border-gold-600 border-t-transparent rounded-full animate-spin"></div>
                                                <span className="ml-2 text-xs text-stone-500">Loading orders...</span>
                                              </div>
                                            ) : orders.length > 0 ? (
                                                <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                                                    <table className="w-full text-left text-xs">
                                                        <thead className="bg-stone-100 text-stone-500 uppercase">
                                                          <tr>
                                                            <th className="px-4 py-3">Order ID</th>
                                                            <th className="px-4 py-3">Date</th>
                                                            <th className="px-4 py-3">Items</th>
                                                            <th className="px-4 py-3">Status</th>
                                                            <th className="px-4 py-3 text-right">Total</th>
                                                          </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-stone-100">
                                                            {orders.slice(0, 5).map(order => (
                                                                <tr key={order.id} className="hover:bg-stone-50">
                                                                    <td className="px-4 py-3 font-mono font-bold text-stone-900">
                                                                        {order.order_number || order.id}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-stone-500">
                                                                        {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : order.date}
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        {order.items?.length || 1} item(s)
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] uppercase font-bold ${getOrderStatusBadge(order.status)}`}>
                                                                            {order.status || 'Pending'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-right font-mono text-stone-900">
                                                                        {formatCurrency(order.total || order.totalAmount || 0)}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                      </table>
                                                    {orders.length > 5 && (
                                                        <div className="px-4 py-2 text-center text-[10px] text-stone-400 border-t border-stone-100">
                                                            + {orders.length - 5} more orders
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-center py-8 bg-white border border-stone-200 rounded-sm">
                                                    <ShoppingBag size={32} className="mx-auto text-stone-300 mb-2" />
                                                    <p className="text-sm text-stone-400 italic">No recent orders found.</p>
                                                    <p className="text-xs text-stone-400 mt-1">Orders will appear here after member makes a purchase.</p>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={8} className="px-6 py-12 text-center">
                                <User size={32} className="mx-auto text-stone-300 mb-3" />
                                <p className="text-stone-500 text-sm">No members found</p>
                                <p className="text-xs text-stone-400 mt-2">
                                    {searchTerm ? 'Try adjusting your search filters' : 'Members will appear here after they register'}
                                </p>
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
              </div>
            )}

            {/* Footer with count */}
            <div className="px-6 py-4 border-t border-stone-100 bg-stone-50/50 flex justify-between items-center">
                <span className="text-xs text-stone-500">
                  Showing {filteredMembers.length} of {localMembers.length} members
                </span>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-stone-400">
                        Last updated: {new Date().toLocaleDateString()}
                    </span>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMembers;